import React from 'react';
import {
    Dialog,
    DialogContent,
    Box,
    Typography,
    Fade,
    Zoom
} from '@mui/material';
import { CheckCircle as CheckCircleIcon, Error as ErrorIcon } from '@mui/icons-material';

interface StatusPopupProps {
    open: boolean;
    success: boolean;
    message: string;
    onClose: () => void;
}

const StatusPopup: React.FC<StatusPopupProps> = ({ open, success, message, onClose }) => {
    React.useEffect(() => {
        if (open) {
            const timer = setTimeout(() => {
                onClose();
            }, 2000);
            return () => clearTimeout(timer);
        }
    }, [open, onClose]);

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="xs"
            fullWidth
            PaperProps={{
                sx: {
                    backgroundColor: 'transparent',
                    boxShadow: 'none',
                    overflow: 'visible'
                }
            }}
        >
            <DialogContent sx={{ p: 0, textAlign: 'center' }}>
                <Fade in={open} timeout={300}>
                    <Box
                        sx={{
                            backgroundColor: 'white',
                            borderRadius: 3,
                            p: 4,
                            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                            border: '1px solid rgba(255,255,255,0.2)',
                            backdropFilter: 'blur(10px)',
                            position: 'relative',
                            overflow: 'hidden'
                        }}
                    >
                        <Zoom in={open} timeout={500} style={{ transitionDelay: '200ms' }}>
                            <Box
                                sx={{
                                    width: 80,
                                    height: 80,
                                    borderRadius: '50%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    mx: 'auto',
                                    mb: 2,
                                    backgroundColor: success ? '#4caf50' : '#f44336',
                                    boxShadow: success 
                                        ? '0 4px 20px rgba(76, 175, 80, 0.3)' 
                                        : '0 4px 20px rgba(244, 67, 54, 0.3)',
                                    animation: 'pulse 2s infinite'
                                }}
                            >
                                {success ? (
                                    <CheckCircleIcon 
                                        sx={{ 
                                            fontSize: 40, 
                                            color: 'white',
                                            animation: 'bounceIn 0.6s ease-out'
                                        }} 
                                    />
                                ) : (
                                    <ErrorIcon 
                                        sx={{ 
                                            fontSize: 40, 
                                            color: 'white',
                                            animation: 'shake 0.6s ease-out'
                                        }} 
                                    />
                                )}
                            </Box>
                        </Zoom>
                        
                        <Fade in={open} timeout={600} style={{ transitionDelay: '400ms' }}>
                            <Typography
                                variant="h6"
                                sx={{
                                    color: success ? '#2e7d32' : '#d32f2f',
                                    fontWeight: 600,
                                    mb: 1
                                }}
                            >
                                {success ? 'Success!' : 'Error!'}
                            </Typography>
                        </Fade>
                        
                        <Fade in={open} timeout={600} style={{ transitionDelay: '500ms' }}>
                            <Typography
                                variant="body2"
                                sx={{
                                    color: '#666',
                                    lineHeight: 1.5
                                }}
                            >
                                {message}
                            </Typography>
                        </Fade>
                    </Box>
                </Fade>
            </DialogContent>
            
            <style>
                {`
                    @keyframes pulse {
                        0% { transform: scale(1); }
                        50% { transform: scale(1.05); }
                        100% { transform: scale(1); }
                    }
                    
                    @keyframes bounceIn {
                        0% { transform: scale(0.3); opacity: 0; }
                        50% { transform: scale(1.05); }
                        70% { transform: scale(0.9); }
                        100% { transform: scale(1); opacity: 1; }
                    }
                    
                    @keyframes shake {
                        0%, 100% { transform: translateX(0); }
                        10%, 30%, 50%, 70%, 90% { transform: translateX(-5px); }
                        20%, 40%, 60%, 80% { transform: translateX(5px); }
                    }
                `}
            </style>
        </Dialog>
    );
};

export default StatusPopup; 